/** @jsx isolateComponent */
import React, { useState } from 'react';

import { Animation } from '../../../theme/Animation';
import { Colors } from '../../../theme/Colors';
import { Link } from '../../navigation/Link';
import { Shadows } from '../../../theme/Shadows';
import { Typography } from '../../../theme/Typography';
import { Icon } from '../../display/Icon';
import type { IconName } from '../../display/Icon';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

type SizeProp = 'medium' | 'large';

type Props = {
  /**
   * Optional content to render beneath the title.
   */
  children?: React.ReactNode;
  /**
   * The route to the thing that is being linked to.
   */
  href: string;
  /**
   * The name of the icon to display.
   */
  icon: IconName;
  /**
   * The size to render the link.
   * @default medium
   */
  size?: SizeProp;
  /**
   * The title of the thing that is being linked to.
   */
  title: string;
};

type DerivedState = {
  isActive: boolean;
};

const defaultProps = {
  size: 'medium' as SizeProp,
};

const Styles = labelStyles('ArticleLink', {
  article: (props: Props & typeof defaultProps, derivedState: DerivedState) => [
    {
      // Colors
      backgroundColor: Colors.grey000,
      color: Colors.grey700,
      // Layout
      alignItems: 'center',
      display: 'grid',
      gap: '10px',
      gridTemplateColumns: 'auto 1fr auto',
      width: '100%',
      height: '100%',
      // Borders
      // This keeps stuff from moving around during `isActive`.
      border: `1px solid transparent`,
      borderRadius: 5,
      // Transitions
      transitionProperty: 'all',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
    },
    withModifier(
      props.size,
      {
        medium: { padding: 10 },
        large: { padding: 15 },
      }[props.size]
    ),
    derivedState.isActive &&
      withModifier('active', {
        backgroundColor: Colors.white,
        border: `1px solid ${Colors.grey200}`,
        borderRadius: 10,
        boxShadow: Shadows.elevation5,
      }),
  ],
  title: (props: Props & typeof defaultProps, derivedState: DerivedState) => [
    {
      color: Colors.black,
      transitionProperty: 'all',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
    },
    withModifier(
      props.size,
      {
        medium: { ...Typography.altText900, fontWeight: 600 },
        large: { ...Typography.subtitle700, fontWeight: 600 },
      }[props.size]
    ),
    derivedState.isActive &&
      withModifier('active', {
        color: Colors.blue500,
      }),
  ],
  icon: ({ iconSize }: { iconSize: number }) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: Colors.white,
      border: `1px solid ${Colors.grey200}`,
    },
    withModifier(`${iconSize}`, {
      borderRadius: Math.ceil(0.25 * iconSize),
      height: iconSize,
      width: iconSize,
    }),
  ],
  arrowContainer: (_props: Props, derivedState: DerivedState) => [
    {
      // Forces child <Icon> to be proper height.
      display: 'grid',
      opacity: 0,
      transitionProperty: 'all',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
    },
    derivedState.isActive &&
      withModifier('active', {
        opacity: 1,
      }),
  ],
});

const sizeToIconSizeMap = {
  medium: 30,
  large: 40,
};

const sizeToArrowSizeMap = {
  medium: 20,
  large: 24,
};

/**
 * Renders a link to an article.
 */
export const ArticleLink = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const [isActive, setActive] = useState(false);
  const derivedState: DerivedState = {
    isActive,
  };
  const iconSize = sizeToIconSizeMap[props.size];
  return (
    <Link
      href={props.href}
      onBlur={() => setActive(false)}
      onFocus={() => setActive(true)}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onMouseUp={() => setActive(false)}
    >
      <article css={Styles.article(props, derivedState)}>
        <span css={Styles.icon({ iconSize })}>
          <Icon
            color="blue500"
            name={props.icon}
            size={Math.ceil(0.6 * iconSize)}
          />
        </span>
        <div>
          <h3 css={Styles.title(props, derivedState)}>{props.title}</h3>
          {props.children}
        </div>
        <div css={Styles.arrowContainer(props, derivedState)}>
          <Icon
            color="blue500"
            name="arrowSmallRight"
            size={sizeToArrowSizeMap[props.size]}
          />
        </div>
      </article>
    </Link>
  );
};

ArticleLink.defaultProps = {
  size: 'medium',
};
